import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

/**
 * Badge compatto che mostra le quote rimanenti per un tipo di upload.
 * kind: "invoices" | "menus" | "recipes"
 * label: etichetta umana ("Fatture AI", "Caricamento menu", "Ricette AI")
 *
 * Si aggiorna automaticamente ascoltando l'evento "renova:quota-refresh"
 * che il resto dell'app può emettere dopo un'azione che consuma crediti.
 */
export function QuotaBadge({ kind, label, className = "" }) {
    const [quota, setQuota] = useState(null);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/billing/quota");
            setQuota(data);
        } catch (e) {
            setQuota(null);
        }
    }, []);

    useEffect(() => {
        load();
        const handler = () => load();
        window.addEventListener("renova:quota-refresh", handler);
        return () => window.removeEventListener("renova:quota-refresh", handler);
    }, [load]);

    if (!quota) return null;

    const planLeft = Math.max(0, (quota[`plan_${kind}`] || 0) - (quota[`${kind}_used`] || 0));
    const extra = quota[`extra_${kind}`] || 0;
    const total = planLeft + extra;
    const exhausted = total === 0;

    return (
        <button
            type="button"
            onClick={() => navigate("/billing")}
            data-testid={`quota-badge-${kind}`}
            title={
                exhausted
                    ? `${label}: nessun credito rimasto questo mese`
                    : `${label}: ${planLeft} piano${extra > 0 ? ` + ${extra} extra` : ""}`
            }
            className={
                "inline-flex items-center gap-1.5 rounded-full border text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 transition " +
                (exhausted
                    ? "border-brand-danger text-brand-danger bg-red-50 hover:bg-red-100"
                    : total <= 2
                    ? "border-brand-warning text-brand-warning bg-amber-50 hover:bg-amber-100"
                    : "border-brand-line text-brand-ink bg-white hover:border-brand-gold/40") +
                " " +
                className
            }
        >
            {exhausted ? (
                <>
                    <AlertTriangle size={11} /> {label}: 0 · Carica ora
                </>
            ) : (
                <>
                    <Zap size={11} className="text-brand-gold" />
                    <span>
                        {label}: <span className="tabular-nums">{total}</span>
                    </span>
                    {extra > 0 && <span className="text-brand-gold">+{extra}</span>}
                </>
            )}
        </button>
    );
}

/**
 * Utility: emette l'evento di refresh quota dopo un'azione che consuma crediti.
 * Chiamalo nei componenti dopo un upload riuscito.
 */
export function refreshQuota() {
    window.dispatchEvent(new CustomEvent("renova:quota-refresh"));
}
